import { Box, Button, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useLayoutEffect, useRef, useState } from "react";
import { ArrowRightIcon, ChatQuestionIcon, PhoneIcon } from "request-form-lib";
import { LogoOnly } from "@/Assets/LogoOnly";
import { alpha } from '@mui/material/styles';

export type SimpleNavigationProps = {
    headline?: string;
    exitLabel?: string;
    helpLabel?: string;
    logoOnly?: boolean;
    onExit?: () => void;
    onHelp?: () => void;
};

const CONTENT_MAX_WIDTH = 1320;
const HORIZONTAL_PADDING = 2;
const VERTICAL_PADDING = "16px";
const MOBILE_PADDING = "10px";

const SimpleNavigation = ({
    headline,
    exitLabel = "Ukončiť",
    helpLabel = "Potrebujete pomôcť?",
    logoOnly = false,
    onExit,
    onHelp,
}: SimpleNavigationProps) => {
    const theme = useTheme();
    const navRef = useRef<HTMLElement>(null);
    const [navHeight, setNavHeight] = useState(0);
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const displayedHelpLabel = isMobile ? "Kontakt" : helpLabel;
    const helpIconColor = theme.palette.primary.main;

    useLayoutEffect(() => {
        const navElement = navRef.current;
        if (!navElement) {
            return;
        }

        const updateNavHeight = () => {
            setNavHeight(navElement.offsetHeight);
        };

        updateNavHeight();

        const resizeObserver = new ResizeObserver(updateNavHeight);
        resizeObserver.observe(navElement);

        return () => {
            resizeObserver.disconnect();
        };
    }, [isMobile, headline, exitLabel, displayedHelpLabel, logoOnly]);

    return (
        <>
            <Box
                ref={navRef}
                component="nav"
                sx={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: theme.zIndex.appBar,
                    width: "100%",
                    backgroundColor: theme.palette.background.default,
                    ...(isMobile && {
                        borderBottom: `1px solid ${theme.palette.divider}`,
                    }),
                }}
            >
                <Box
                    sx={{
                        width: "100%",
                        maxWidth: CONTENT_MAX_WIDTH,
                        marginX: "auto",
                        paddingX: isMobile ? MOBILE_PADDING : HORIZONTAL_PADDING,
                        paddingY: isMobile ? MOBILE_PADDING : VERTICAL_PADDING,
                        boxSizing: "border-box",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: logoOnly ? "flex-start" : "space-between",
                        gap: 2,
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-start",
                            gap: 2,
                            minWidth: 0,
                            flex: logoOnly ? "0 0 auto" : 1,
                        }}
                    >
                        <LogoOnly width="auto" height={isMobile ? 35 : 40} />

                        {!logoOnly && !isMobile && (
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "flex-start",
                                    gap: 0,
                                    minWidth: 0,
                                }}
                            >
                                {headline && (
                                    <Typography
                                        variant="h6"
                                        component="h1"
                                        sx={{
                                            fontWeight: 700,
                                            color: "#000",
                                            fontFeatureSettings: "'liga' off, 'clig' off",
                                        }}
                                    >
                                        {headline}
                                    </Typography>
                                )}

                                <Box
                                    component="button"
                                    type="button"
                                    onClick={onExit}
                                    sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                        p: 0,
                                        m: 0,
                                        border: "none",
                                        background: "none",
                                        cursor: "pointer",
                                        textAlign: "left",
                                        ...theme.typography.body2,
                                        color: theme.palette.primary.main,
                                        fontFeatureSettings: "'liga' off, 'clig' off",
                                        "&:hover": {
                                            textDecoration: "underline",
                                        },
                                        borderRadius: "4px",
                                        padding: "0 2px",
                                        transition: "background-color 0.15s ease",
                                        "&:focus-visible": {
                                            outline: "none",
                                            backgroundColor: alpha(theme.palette.primary.main, 0.12),
                                        },
                                    }}
                                >
                                    <Box
                                        component="span"
                                        sx={{
                                            display: "inline-flex",
                                            lineHeight: 0,
                                            transform: "rotate(180deg)",
                                        }}
                                    >
                                        <ArrowRightIcon
                                            width={18}
                                            height={18}
                                            color={theme.palette.primary.main}
                                        />
                                    </Box>
                                    {exitLabel}
                                </Box>
                            </Box>
                        )}
                    </Box>

                    {!logoOnly && (
                        <Button
                            variant="outlined"
                            onClick={onHelp}
                            startIcon={
                                isMobile ? (
                                    <PhoneIcon
                                        width={16}
                                        height={16}
                                        color={helpIconColor}
                                    />
                                ) : (
                                    <ChatQuestionIcon
                                        width={16}
                                        height={16}
                                        color={helpIconColor}
                                    />
                                )
                            }
                            sx={{
                                flexShrink: 0,
                                textTransform: "none",
                                textAlign: "center",
                                borderColor: theme.palette.primary.main,
                                color: theme.palette.primary.main,
                                paddingX: "12px",
                                paddingY: "9px",
                                minHeight: "unset",
                                ...theme.typography.body2,
                                fontWeight: 500,
                                fontFeatureSettings: "'liga' off, 'clig' off",
                                "& .MuiButton-startIcon": {
                                    marginLeft: 0,
                                    marginRight: 1,
                                    color: theme.palette.primary.main,
                                },
                                "&:hover": {
                                    borderColor: theme.palette.primary.main,
                                    backgroundColor: theme.palette.action.hover,
                                },
                            }}
                        >
                            {displayedHelpLabel}
                        </Button>
                    )}
                </Box>
            </Box>
            <Box aria-hidden sx={{ height: navHeight, flexShrink: 0 }} />
        </>
    );
};

export default SimpleNavigation;
